import { HomeReview, ReviewItem, ReviewResponse, ReviewTemplate } from '../types';
import { dedupeResponsesByItemId } from './reviewResponseSelector';

export type OfflineReviewRecord = any;
export type OfflineResponseRecord = any;

export function mapOfflineReviewToHomeReview(
  offlineReview: OfflineReviewRecord,
  template?: ReviewTemplate
): HomeReview {
  const homeReview = {
    id: offlineReview.id,
    propertyId: offlineReview.propertyId,
    templateId: offlineReview.templateId,
    reviewerId: offlineReview.reviewerId,
    status: offlineReview.status,
    startedAt: offlineReview.startedAt,
    completedAt: offlineReview.completedAt,
    submittedAt: offlineReview.submittedAt,
    notes: offlineReview.notes,
    onDemandProjects: offlineReview.onDemandProjects,
    vendorNotes: offlineReview.vendorNotes,
    metadata: offlineReview.metadata,
    syncStatus: offlineReview.syncStatus,
    syncError: offlineReview.syncError,
    createdAt: offlineReview.createdAt,
    updatedAt: offlineReview.updatedAt,
    address: offlineReview.address,
    memberName: offlineReview.memberName,
    memberEmail: offlineReview.memberEmail,
    homeManagerName: offlineReview.homeManagerName,
    homeManagerEmail: offlineReview.homeManagerEmail,
    property: offlineReview.address
      ? {
        id: offlineReview.propertyId,
        address: offlineReview.address,
      }
      : undefined,
    responses: [],
  } as HomeReview;

  if (template) {
    homeReview.template = template;
  }

  return homeReview;
}

export function mapOfflineReviewsToHomeReviews(
  offlineReviews: OfflineReviewRecord[]
): HomeReview[] {
  return offlineReviews.map(review => mapOfflineReviewToHomeReview(review));
}

export function mapOfflineResponseToReviewResponse(
  offlineResponse: OfflineResponseRecord
): ReviewResponse {
  const response = {
    id: offlineResponse.id,
    reviewId: offlineResponse.reviewId,
    itemId: offlineResponse.itemId,
    roomId: offlineResponse.roomId,
    floor: offlineResponse.floor,
    tempId: offlineResponse.tempId,
    isPresent: offlineResponse.isPresent,
    status: offlineResponse.status,
    responseData: offlineResponse.responseData,
    notes: offlineResponse.notes,
    findings: offlineResponse.findings || [],
    photos: offlineResponse.photos || [],
    createdAt: offlineResponse.createdAt,
    updatedAt: offlineResponse.updatedAt,
    syncStatus: offlineResponse.syncStatus,
  } as ReviewResponse;

  if (offlineResponse.itemSnapshot) {
    (response as any).item = offlineResponse.itemSnapshot;
  }

  return response;
}

export function mergeOfflineChangesWithApiData(
  apiReview: HomeReview,
  offlineReview?: HomeReview | null
): HomeReview {
  const mergedReview = {
    ...apiReview,
    responses: dedupeResponsesByItemId(apiReview.responses),
  };

  if (!offlineReview) {
    return mergedReview;
  }

  const hasPendingReviewChanges =
    (offlineReview as any).syncStatus === 'pending' ||
    (offlineReview as any).syncStatus === 'failed';

  if (
    offlineReview.notes !== undefined &&
    (hasPendingReviewChanges || offlineReview.notes !== apiReview.notes)
  ) {
    mergedReview.notes = offlineReview.notes;
  }

  if (
    offlineReview.onDemandProjects !== undefined &&
    (hasPendingReviewChanges || offlineReview.onDemandProjects !== apiReview.onDemandProjects)
  ) {
    mergedReview.onDemandProjects = offlineReview.onDemandProjects;
  }

  if (
    offlineReview.vendorNotes !== undefined &&
    (hasPendingReviewChanges || offlineReview.vendorNotes !== apiReview.vendorNotes)
  ) {
    mergedReview.vendorNotes = offlineReview.vendorNotes;
  }

  if (offlineReview.responses && offlineReview.responses.length > 0) {
    const apiResponsesMap = new Map();
    if (apiReview.responses) {
      dedupeResponsesByItemId(apiReview.responses).forEach(response => {
        apiResponsesMap.set(response.itemId, response);
      });
    }

    offlineReview.responses.forEach(offlineResponse => {
      const apiResponse = apiResponsesMap.get(offlineResponse.itemId);

      if (apiResponse) {
        const hasOfflineChanges = hasOfflineResponseChanges(offlineResponse, apiResponse);

        if (hasOfflineChanges) {
          apiResponse.responseData = {
            ...apiResponse.responseData,
            ...offlineResponse.responseData,
          };

          if (offlineResponse.notes !== undefined) {
            apiResponse.notes = offlineResponse.notes;
          }

          if (offlineResponse.status !== undefined && offlineResponse.status !== null) {
            apiResponse.status = offlineResponse.status;
          }

          if (offlineResponse.photos && offlineResponse.photos.length > 0) {
            apiResponse.photos = mergePhotos(apiResponse.photos || [], offlineResponse.photos);
          }
        }
      } else {
        mergedReview.responses = mergedReview.responses || [];
        mergedReview.responses.push(offlineResponse);
      }
    });
  }

  const offlineOnDemandItems = extractOnDemandItemsFromOfflineReview(offlineReview);
  if (offlineOnDemandItems.length > 0) {
    const serverOnDemandItems = (mergedReview as any).onDemandItems || [];
    const existingIds = new Set(serverOnDemandItems.map((item: ReviewItem) => item.id));
    const mergedOnDemandItems = [...serverOnDemandItems];

    offlineOnDemandItems.forEach((offlineItem) => {
      const itemId = (offlineItem as any).id || (offlineItem as any).tempId;
      if (itemId && existingIds.has(itemId)) {
        return;
      }
      mergedOnDemandItems.push(offlineItem);
    });

    (mergedReview as any).onDemandItems = mergedOnDemandItems;

    if (mergedReview.template?.categories) {
      offlineOnDemandItems.forEach((offlineItem) => {
        const targetCategoryId = offlineItem.categoryId;
        if (!targetCategoryId) {
          return;
        }

        const category = mergedReview.template!.categories!.find(
          (cat: any) => Number(cat.id) === Number(targetCategoryId)
        );
        if (!category) {
          return;
        }

        category.items = category.items || [];
        const alreadyExists = category.items.some(
          (item: any) =>
            item.id === offlineItem.id || item.tempId === (offlineItem as any).tempId
        );
        if (!alreadyExists) {
          category.items.push({
            ...offlineItem,
            isOnDemand: true,
          });
        }
      });
    }
  }

  return mergedReview;
}

export function extractOnDemandItemsFromOfflineReview(
  offlineReview: HomeReview
): ReviewItem[] {
  const offlineItems: ReviewItem[] = [];

  const reviewOnDemand = (offlineReview as any).onDemandItems;
  if (Array.isArray(reviewOnDemand)) {
    reviewOnDemand.forEach((item) => {
      if (!offlineItems.find(existing => existing.id === item.id || (existing as any).tempId === item.tempId)) {
        offlineItems.push(item);
      }
    });
  }

  if (offlineReview.template?.categories) {
    const collectItems = (categories: any[]) => {
      categories.forEach((category) => {
        if (category.items) {
          category.items.forEach((item: any) => {
            if (!item?.isOnDemand) {
              return;
            }

            if (
              offlineItems.find(
                existing => existing.id === item.id || (existing as any).tempId === item.tempId
              )
            ) {
              return;
            }

            offlineItems.push({
              id: item.id,
              tempId: item.tempId,
              categoryId: item.categoryId || category.id,
              name: item.name,
              description: item.description,
              questionsSchema: item.questionsSchema,
              displayOrder: item.displayOrder,
              isRequired: item.isRequired,
              defaultPresent: item.defaultPresent,
              isOnDemand: true,
              homeReviewId: offlineReview.id,
            } as ReviewItem);
          });
        }

        if (category.subcategories) {
          collectItems(category.subcategories);
        }
      });
    };

    collectItems(offlineReview.template.categories);
  }

  return offlineItems;
}

export function hasOfflineResponseChanges(
  offlineResponse: Partial<ReviewResponse>,
  apiResponse: Partial<ReviewResponse>
): boolean {
  if (offlineResponse.responseData) {
    if (!apiResponse.responseData) {
      return true;
    }

    const offlineKeys = Object.keys(offlineResponse.responseData);
    for (const key of offlineKeys) {
      if (JSON.stringify(offlineResponse.responseData[key]) !== JSON.stringify(apiResponse.responseData[key])) {
        return true;
      }
    }
  }

  if (offlineResponse.notes !== apiResponse.notes) {
    return true;
  }

  if (offlineResponse.status !== apiResponse.status) {
    return true;
  }

  if ((offlineResponse as any).photos && (offlineResponse as any).photos.length > 0) {
    if (!(apiResponse as any).photos || (apiResponse as any).photos.length === 0) {
      return true;
    }
  }

  return false;
}

export function mergePhotos(apiPhotos: any[], offlinePhotos: any[]): any[] {
  const merged = [...apiPhotos];

  offlinePhotos.forEach(offlinePhoto => {
    const existsInApi = apiPhotos.some(apiPhoto =>
      apiPhoto.id === offlinePhoto.id ||
      apiPhoto.url === offlinePhoto.url
    );

    if (!existsInApi) {
      merged.push(offlinePhoto);
    }
  });

  return merged;
}
